import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FileView from "../../../../components/FileView";
import { updateCaptionTodo } from "../../../../containers/Diaries/actions";
import { getUserInfo } from "../../../SpockViewPage/actions/action";
import { formatTimestamp, getUniqueTagsWithSystemFlag, PriorityFlag, renderModuleType } from "../../util";
import Icon from "../../../../icons";
import UserImage from '../../../../components/UserImage/index';

const NoDataFound = () => (
    <div className="noDataFound noFoundDiary label">
        <h2>No Data Found</h2>
    </div>
);

const StatusDateRow = ({ status, currentStatus, label, date }) => {
    if (currentStatus !== status || !date) return null;
    return (
        <div data-test={label} className="reminder mb mt">
            <label>{label}</label>
            <date>{date}</date>
        </div>
    );
};

const OwnerContact = ({ phone, email }) => (
    <li data-test="Contact Details">
        <div className="rowFlex wrapFlex alignMiddle">
            <span className="mr__1 alignMiddle">
                <Icon name="mobile" className="mobile svg-call icon mr" />
                {phone || 'NA'}
            </span>
            <span className="alignMiddle">
                <Icon name="email" className="email svg-email icon mr" />
                {email || 'NA'}
            </span>
        </div>
    </li>
);

const DiaryDetail = () => {
    const dispatch  = useDispatch();
    const timerRef  = useRef(null);

    const { listingData, selectedDiary } = useSelector(state => state.diaries.listing);
    const diaryDetails = listingData?.data?.find(d => d.todo_id === selectedDiary) ?? null;

    const files = diaryDetails?.file_guid ? {
        Files: [
            {
                FileGuid:     diaryDetails?.file_guid,
                FileName:     diaryDetails?.file_name,
                Caption:      diaryDetails?.file_caption,
                FileType:     diaryDetails?.file_type,
                FileSize:     "",
                FileUrl:      "",
                FilePreviewUrl: ""
            }
        ]
    } : {};

    const fullName  = `${diaryDetails?.owner?.first_name} ${diaryDetails?.owner?.last_name}`.trim();
    
    // Tags/Systemtags handler
    const uniqueTags = getUniqueTagsWithSystemFlag(diaryDetails?.tags, diaryDetails?.system_tags);
    
    // Handlers
    const openMiniProfile = useCallback((targetRect) => {
        if (!diaryDetails?.owner_id) return;
        dispatch(getUserInfo({ target: targetRect, userId: diaryDetails?.owner_id }));
    }, [dispatch, diaryDetails?.owner_id]);

    const onMouseEnter = useCallback((e) => {
        const targetRect = e.currentTarget.getBoundingClientRect();
        timerRef.current = setTimeout(() => openMiniProfile(targetRect), 500);
    }, [openMiniProfile]);

    const onMouseLeave = useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);

    // Cleanup timer on unmount
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const updateCaption = useCallback((data) => {
        dispatch(updateCaptionTodo(data));
    }, [dispatch]);

    if (!diaryDetails) return <NoDataFound />;
    
    return (
        <div className="scrollArea detailsData">

            <div className="wkDetailInfo">
                <div className="diaryWkTitle alignMiddle">
                    {renderModuleType(diaryDetails, true)}
                </div>
                <ul className="rowFlex wrapFlex">
                    <li data-test="Diary Owner">
                        <span className="userGroup diaryUser">
                            <div className="rowFlex">
                                <div
                                    className="userPic userPicIcon"
                                    onMouseEnter={onMouseEnter}
                                    onMouseLeave={onMouseLeave}
                                >
                                    <UserImage
                                        profileUrl={diaryDetails?.owner?.image?.progile}
                                        className="profilePic"
                                        profileName={fullName}
                                    />
                                </div>
                                <div className="userInfo flexWrap">
                                    <span>{fullName}</span>
                                    <label>Diary Owner</label>
                                </div>
                            </div>
                        </span>
                    </li>
                    <OwnerContact phone={diaryDetails?.owner?.phone} email={diaryDetails?.owner?.email} />
                </ul>
            </div>

            <div className="wkDetailInfo">
                <ul className="rowFlex wrapFlex">
                    <li data-test="Priority">
                        <label>Priority</label>
                        <span>
                            {`${diaryDetails?.priority_title}`}
                            <PriorityFlag
                                priority={diaryDetails?.priority_title}
                                isCompleted={diaryDetails?.completed}
                                status={diaryDetails?.status}
                            />
                        </span>
                    </li>
                    <li data-test="Due On">
                        <label>Due On</label>
                        <span>
                            {diaryDetails?.due_date
                                ? formatTimestamp(diaryDetails?.due_date, diaryDetails?.due_date_tz, "datetime")
                                : "-"}
                        </span>
                    </li>
                    <li data-test="Reminder On" className="mb">
                        <label>Reminder On</label>
                        <span>
                            {diaryDetails?.PreetyReminderDate //todo
                                ? formatTimestamp(diaryDetails?.due_date, diaryDetails?.ReminderTz, "datetime")
                                : "-"}
                        </span>
                    </li>
                </ul>
            </div>

            {/* Status dates */}
            <StatusDateRow
                status="COMPLETED"
                currentStatus={diaryDetails?.status}
                label="Completed On"
                date={diaryDetails?.PreetyCompletedDate} //todo
            />
            <StatusDateRow
                status="CANCELLED"
                currentStatus={diaryDetails?.status}
                label="Cancelled On"
                date={diaryDetails?.PreetyDeletedDate} //todo
            />

            <div data-test="Tags" className="mb formTags">
                <label>Tags</label>
                {
                    uniqueTags.map(({ tag, isSystemTag }) => (
                        <label key={tag} className={`mr status tags ${isSystemTag ? "ReactTags__tag" : ""}`}>{tag}</label>
                    ))
                }
            </div>

            <div data-test="Attachment" className="attachMent mb">
                <label>Attachment</label>
                {Object.keys(files).length > 0 ? (
                    <div className="fileAttachment addFile">
                        <FileView
                            fileData={files}
                            galleryData={diaryDetails?.fileData?.gallery ?? []}
                            updateFileCaption={updateCaption}
                        />
                    </div>
                ) : (
                    <p>No Files available</p>
                )}
            </div>

            <div data-test="description" className="description">
                <label>Description</label>
                {diaryDetails?.description
                    ? <p className="descriptionFormating" dangerouslySetInnerHTML={{ __html: diaryDetails?.description }} />
                    : "-"
                }
            </div>
        </div>
    );
};

export default DiaryDetail;